import emailjs from "@emailjs/browser";
import axios from "axios";

const EMAILJS_PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const EMAILJS_SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEHOLDER_KEYS = new Set([
  undefined, null, "",
  "YOUR_EMAILJS_PUBLIC_KEY", "YOUR_PUBLIC_KEY",
  "service_physiosewa",       // default placeholder service id
  "template_booking",         // default placeholder template id
]);

const PHONE_LINE = "9871053777 / 9217221357";

let initialised = false;
const initEmailJS = () => {
  if (initialised) return;
  if (PLACEHOLDER_KEYS.has(EMAILJS_PUBLIC_KEY)) return;
  emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });
  initialised = true;
};

export const isEmailJsConfigured = () =>
  !PLACEHOLDER_KEYS.has(EMAILJS_PUBLIC_KEY) &&
  !PLACEHOLDER_KEYS.has(EMAILJS_SERVICE_ID) &&
  !PLACEHOLDER_KEYS.has(EMAILJS_TEMPLATE_ID);

/**
 * submitBooking: shared booking submit logic for Landing + SEO pages.
 *  1. Pre-open a placeholder WhatsApp tab synchronously (within the user click)
 *     so popup blockers don't intercept after the await.
 *  2. emailjs.send → mails info@physiosewa.in (when keys are configured)
 *  3. fire-and-forget POST /api/appointments to keep a server-side record
 *  4. Set the pre-opened tab's location to the WhatsApp deep-link with the
 *     prefilled booking summary.
 *
 * Returns a Promise that resolves once the booking has been processed.
 * Throws on EmailJS failure so the caller can show an error banner.
 */
export async function submitBooking(form) {
  initEmailJS();

  // 1) Pre-open WhatsApp window synchronously to bypass popup blockers
  let waWindow = null;
  try { waWindow = window.open("about:blank", "_blank"); } catch (_) { /* blocked */ }

  const submittedAt = new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  const visitLabel = form.visit_type === "home" ? "Home Visit" : "At Clinic";

  const templateParams = {
    patient_name:   form.full_name,
    phone:          form.phone,
    email:          form.email || "Not provided",
    service:        form.service,
    visit_type:     visitLabel,
    preferred_date: form.preferred_date,
    preferred_time: form.preferred_time || "Not specified",
    address:        form.address || "N/A (At Clinic)",
    notes:          form.notes   || "None",
    submitted_at:   submittedAt,
  };

  try {
    // 2) EmailJS — only attempt when keys are configured
    if (isEmailJsConfigured()) {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams);
    }
  } catch (err) {
    // Close the pre-opened blank tab so the user isn't left with garbage tabs
    if (waWindow && !waWindow.closed) waWindow.close();
    throw err;
  }

  // 3) Persist to backend (fire-and-forget; never block UX on this)
  axios
    .post(`${API}/appointments`, {
      full_name: form.full_name,
      phone: form.phone,
      service: form.service,
      visit_type: form.visit_type,
      preferred_date: form.preferred_date,
      preferred_time: form.preferred_time || null,
      notes: form.notes || null,
    })
    .catch((e) => {
      // Surface in dev console so an outage isn't silent forever
      // eslint-disable-next-line no-console
      console.warn("[bookingService] backend save failed:", e?.message || e);
    });

  // 4) Navigate the pre-opened tab to WhatsApp deep-link
  const waMsg =
    `🏥 NEW BOOKING — PhysioSewa\n\n` +
    `👤 ${templateParams.patient_name}\n` +
    `📞 ${templateParams.phone}\n` +
    `🩺 ${templateParams.service}\n` +
    `🏠 ${templateParams.visit_type}\n` +
    `📅 ${templateParams.preferred_date}\n` +
    `⏰ ${templateParams.preferred_time}\n` +
    `🕐 ${templateParams.submitted_at}\n\n` +
    `(Reply to confirm. PhysioSewa: ${PHONE_LINE})`;
  const waUrl = `https://wa.me/919100053777?text=${encodeURIComponent(waMsg)}`;
  if (waWindow && !waWindow.closed) {
    try { waWindow.location.href = waUrl; } catch (_) { /* cross-origin */ }
  } else {
    // Popup was blocked — fall back to a regular new-tab open
    try { window.open(waUrl, "_blank"); } catch (_) { /* ignore */ }
  }
}
